import type { Firestore } from "@google-cloud/firestore";

import {
  FetchPupilResponseArgs,
  GoogleClassroomClient,
} from "@/services/GoogleClassroom";
import { EncryptionService } from "@/services/Encryption";
import { FirestoreClient } from "@/services/FirestoreClient";
import { GoogleOAuthClient } from "@/services/GoogleOAuth";
import { OakOAuth } from "@/services/OakGoogleClassroom/OakOAuth";
import { OakSessions } from "@/services/OakGoogleClassroom/OakSessions";
import { OakAttachment } from "@/services/OakGoogleClassroom/OakAttachments";
import { OakPupilProgress } from "@/services/OakGoogleClassroom/OakPupilProgress";
import { OakCourseWork } from "@/services/OakGoogleClassroom/OakCourseWork";
import { OakCourseWorkProgress } from "@/services/OakGoogleClassroom/OakCourseWorkProgress";
import {
  ClassroomAttachmentEntity,
  ClassroomCourseWorkEntity,
  Code,
  CourseListItem,
  CourseWorkCreatedResult,
  CourseWorkPupilProgress,
  CreateAnnouncementAttachmentArgs,
  CreateCourseWorkServiceArgs,
  PupilLessonProgress,
  UpsertCourseWorkPupilProgressArgs,
  UpsertPupilLessonProgressArgs,
} from "@/types/services";
import { OakGoogleClassroomException } from "@/errors";

type ResolvedTeacherOAuthClient = {
  oauthClient: GoogleOAuthClient;
  courseWork: ClassroomCourseWorkEntity;
};

export type VerifiedAuthSession = {
  session: string;
  token: string;
  userProfilePicUrl?: string;
  loginHint?: string;
};

export type OakGoogleSignInCallback = {
  encryptedSession: string;
  accessToken: string;
  profilePictureUrl?: string;
};

export class OakGoogleClassroomAddOn {
  private oauthService: OakOAuth;
  private sessionService: OakSessions;
  private classroomAttachmentService: OakAttachment;
  private pupilProgressService: OakPupilProgress;
  private courseWorkService: OakCourseWork;
  private courseWorkProgressService: OakCourseWorkProgress;
  private encryptionService: EncryptionService;
  private firestoreClient: FirestoreClient;
  private baseUrl: string;

  constructor(
    encryptionSecret: string,
    firestore: Firestore,
    googleOAuthClientId: string,
    googleOAuthClientSecret: string,
    googleOAuthCallbackApiRoute: string,
    sessionSecret: string,
    baseUrl: string,
  ) {
    this.baseUrl = baseUrl;
    this.encryptionService = new EncryptionService(encryptionSecret);
    this.firestoreClient = new FirestoreClient(firestore);
    this.oauthService = new OakOAuth(
      googleOAuthClientId,
      googleOAuthClientSecret,
      googleOAuthCallbackApiRoute,
    );
    this.sessionService = new OakSessions(
      sessionSecret,
      this.encryptionService,
      this.firestoreClient,
    );
    this.classroomAttachmentService = new OakAttachment(
      baseUrl,
      this.firestoreClient,
    );
    this.pupilProgressService = new OakPupilProgress(
      baseUrl,
      this.firestoreClient,
    );
    this.courseWorkService = new OakCourseWork(baseUrl, this.firestoreClient);
    this.courseWorkProgressService = new OakCourseWorkProgress(
      baseUrl,
      this.firestoreClient,
    );
  }

  /**
   * Generates the Google OAuth sign-in URL for user authentication.
   * This requests the scopes for a teacher account
   * @param loginHint - Optional login hint to attach to generated url
   * @param subscribeToNewsletter - Whether the user opted in to the newsletter
   */
  public async getGoogleSignInUrl(
    loginHint?: string,
    subscribeToNewsletter: boolean = false,
  ): Promise<string> {
    return this.oauthService.getSignInUrl(loginHint, subscribeToNewsletter);
  }

  /**
   * Generates the Google OAuth sign-in URL for pupil authentication.
   * This requests the scopes for a pupil, which are more limited than the teacher scopes.
   * @param loginHint
   */
  public async getPupilGoogleSignInUrl(loginHint?: string): Promise<string> {
    return this.oauthService.getSignInUrl(loginHint, false, true);
  }

  /**
   * Handles the Google OAuth callback by exchanging the authorization code for tokens,
   * creating an encrypted session, and persisting user credentials.
   * @param code - The authorization code from Google's OAuth callback
   * @param subscribeToNewsletterFn - Optional callback to subscribe the user to a newsletter
   */
  public async handleGoogleSignInCallback(
    code: Code,
    subscribeToNewsletterFn?: (email: string) => Promise<void>,
  ): Promise<OakGoogleSignInCallback> {
    const parsedUser = await this.oauthService.exchangeCodeForTokens(code);

    const { encryptedRefreshToken, encryptedProfileUrl } =
      this.sessionService.encryptCredentials(
        parsedUser.refreshToken,
        parsedUser.profilePictureUrl,
      );

    const encryptedSession = await this.sessionService.sealSession(
      parsedUser.loginHint,
      parsedUser.accessToken,
    );

    await this.sessionService.saveUserCredentials({
      ...(encryptedRefreshToken && { refreshToken: encryptedRefreshToken }),
      profilePictureUrl: encryptedProfileUrl,
      loginHint: parsedUser.loginHint,
    });

    if (subscribeToNewsletterFn && parsedUser.email)
      await subscribeToNewsletterFn(parsedUser.email);

    return {
      encryptedSession,
      accessToken: parsedUser.accessToken,
      profilePictureUrl: parsedUser.profilePictureUrl,
    };
  }

  /**
   * Checks if session exists and is authenticated. It will automatically attempt
   * to refresh the session if it has expired.
   * Returns null for an invalid session.
   * Returns the encrypted session (original or refreshed) if valid.
   * @param encryptedSession - The user's encrypted session string
   * @param accessToken - The user's current OAuth access token
   */
  public async verifyAuthSession(
    encryptedSession?: string,
    accessToken?: string,
  ): Promise<VerifiedAuthSession | null> {
    if (!encryptedSession || !accessToken) return null;

    const sessionAndUser =
      await this.sessionService.getSessionAndUser(encryptedSession);
    if (!sessionAndUser || sessionAndUser.session.accessToken !== accessToken)
      return null;

    const result = await this.oauthService.validateOrRefreshToken(
      accessToken,
      sessionAndUser.user.refreshToken,
    );
    if (!result) return null;

    const session = result.wasRefreshed
      ? await this.sessionService.sealSession(
          sessionAndUser.session.loginHint,
          result.token,
        )
      : encryptedSession;

    return {
      session,
      token: result.token,
      userProfilePicUrl: sessionAndUser.user.profilePictureUrl,
      loginHint: sessionAndUser.session.loginHint,
    };
  }

  /**
   * Validates the user's session and creates a Google Classroom attachment.
   * @param args - The attachment including course, item, and lesson details
   * @param accessToken - The user's current OAuth access token
   * @param session - The user's encrypted session string
   */
  public async createAttachment(
    args: CreateAnnouncementAttachmentArgs,
    accessToken: string,
    session: string,
  ): Promise<ClassroomAttachmentEntity | null> {
    const sessionAndUser = await this.sessionService.getSessionAndUser(session);
    if (!sessionAndUser || accessToken !== sessionAndUser.session.accessToken)
      throw OakGoogleClassroomException.OakGoogleClassroomError(
        "Invalid Google Classroom session",
        "createAttachment",
        { args },
      );

    const oauthClient = this.oauthService.createClient(
      accessToken,
      sessionAndUser.user.refreshToken,
    );

    return this.classroomAttachmentService.createAttachment(
      { ...args, teacherLoginHint: sessionAndUser.session.loginHint },
      oauthClient,
    );
  }

  /**
   * Gets the add-on context from Google Classroom for the current pupil.
   * Returns the studentContext including submissionId, plus the pupilLoginHint
   * extracted from the session.
   * @param courseId - The Google Classroom course ID
   * @param itemId - The courseWork item ID
   * @param attachmentId - The attachment ID
   * @param accessToken - The pupil's current OAuth access token
   * @param session - The pupil's encrypted session string
   */
  public async getAddOnContext(
    courseId: string,
    itemId: string,
    attachmentId: string,
    accessToken: string,
    session: string,
  ) {
    const sessionAndUser = await this.sessionService.getSessionAndUser(session);
    if (!sessionAndUser || accessToken !== sessionAndUser.session.accessToken)
      throw OakGoogleClassroomException.OakGoogleClassroomError(
        "Invalid Google Classroom session",
        "getAddOnContext",
      );

    const oauthClient = this.oauthService.createClient(
      accessToken,
      sessionAndUser.user.refreshToken,
    );

    const context = await this.pupilProgressService.getAddOnContext(
      courseId,
      itemId,
      attachmentId,
      oauthClient,
    );

    return {
      ...context,
      pupilLoginHint: sessionAndUser.session.loginHint,
    };
  }

  /**
   * Resolves a teacher OAuth client by looking up the attachment's teacherLoginHint
   * and fetching the teacher's stored credentials from Firestore.
   * Grade sync is intentionally skipped when the attachment's maxPoints is not a
   * positive number (null, undefined, or 0), allowing teachers to disable automatic
   * grading by setting the score to 0 or Unmarked.
   * In dev mode, falls back to GOOGLE_CLASSROOM_DEV_REFRESH_TOKEN if teacher credentials are not found.
   * @param attachmentId - The attachment ID to look up the teacher for.
   * @returns A GoogleOAuthClient authenticated with teacher credentials, or undefined if grade sync is disabled or credentials are unavailable.
   */
  private async resolveTeacherOAuthClient(
    attachmentId: string,
  ): Promise<GoogleOAuthClient | undefined> {
    const attachment =
      await this.firestoreClient.getClassroomAttachmentById(attachmentId);

    if (!attachment) {
      console.warn(
        `Attachment ${attachmentId} not found, skipping grade submission`,
      );
      return undefined;
    }

    if (typeof attachment.maxPoints !== "number" || attachment.maxPoints <= 0) {
      console.warn(
        `Grade sync is disabled for attachment ${attachmentId} (maxPoints is not a positive value), skipping grade submission`,
      );
      return undefined;
    }

    const teacherLoginHint = attachment.teacherLoginHint;

    if (!teacherLoginHint) {
      console.warn(
        `No teacherLoginHint found on attachment ${attachmentId}, skipping grade submission`,
      );
      return undefined;
    }

    const refreshToken = await this.getTeacherRefreshToken(teacherLoginHint);
    if (!refreshToken) return undefined;

    return this.oauthService.createClient(undefined, refreshToken);
  }

  /**
   * Upserts pupil lesson progress to Firestore and updates GC pointsEarned
   * when the exit quiz is newly completed, using the teacher's OAuth credentials.
   * @param args - The lesson progress data
   * @param accessToken - The pupil's current OAuth access token
   * @param session - The pupil's encrypted session string
   */
  public async upsertPupilLessonProgress(
    args: UpsertPupilLessonProgressArgs,
    accessToken: string,
    session: string,
  ): Promise<PupilLessonProgress> {
    const sessionAndUser = await this.sessionService.getSessionAndUser(session);
    if (!sessionAndUser || accessToken !== sessionAndUser.session.accessToken)
      throw OakGoogleClassroomException.OakGoogleClassroomError(
        "Invalid Google Classroom session",
        "upsertPupilLessonProgress",
        { args },
      );

    const teacherOAuthClient = await this.resolveTeacherOAuthClient(
      args.attachmentId,
    );

    return this.pupilProgressService.upsertPupilLessonProgress(
      args,
      teacherOAuthClient,
    );
  }

  /**
   * Retrieves pupil lesson progress from Firestore.
   * Does not require Google Classroom authentication.
   * @param submissionId - The Google Classroom submission ID
   */
  public async getPupilLessonProgress(
    submissionId: string,
    attachmentId: string,
    itemId: string,
  ): Promise<PupilLessonProgress | null> {
    return this.pupilProgressService.getPupilLessonProgress(
      submissionId,
      attachmentId,
      itemId,
    );
  }

  /**
   * Resolves a teacher OAuth client by looking up the coursework's teacherLoginHint
   * from Firestore (keyed by assignmentToken) and fetching stored credentials.
   */
  private async resolveTeacherOAuthClientForCourseWork(
    assignmentToken: string,
  ): Promise<ResolvedTeacherOAuthClient | undefined> {
    const courseWork =
      await this.firestoreClient.getClassroomCourseWork(assignmentToken);

    if (!courseWork) {
      console.warn(
        `CourseWork not found for assignmentToken=${assignmentToken}, skipping grade submission`,
      );
      return undefined;
    }

    const refreshToken = await this.getTeacherRefreshToken(
      courseWork.teacherLoginHint,
    );
    if (!refreshToken) return undefined;

    return {
      oauthClient: this.oauthService.createClient(undefined, refreshToken),
      courseWork,
    };
  }

  private async getTeacherRefreshToken(
    teacherLoginHint: string,
  ): Promise<string | undefined> {
    const teacherCredentials =
      await this.firestoreClient.getConnectedUserCredentials(teacherLoginHint);

    let refreshToken: string | undefined;

    if (teacherCredentials?.refreshToken) {
      refreshToken = this.encryptionService.decryptString(
        teacherCredentials.refreshToken,
      );
    }

    // In dev, fall back to the dev refresh token if teacher credentials are not in the emulator
    if (!refreshToken && process.env.GOOGLE_CLASSROOM_DEV_REFRESH_TOKEN) {
      refreshToken = process.env.GOOGLE_CLASSROOM_DEV_REFRESH_TOKEN;
    }

    if (!refreshToken) {
      console.warn(
        `No refresh token available for teacher ${teacherLoginHint}, skipping grade submission`,
      );
      return undefined;
    }

    return refreshToken;
  }

  /**
   * Lists the active Google Classroom courses for the authenticated teacher.
   * @param accessToken - The teacher's current OAuth access token
   * @param session - The teacher's encrypted session string
   */
  public async listCourses(
    accessToken: string,
    session: string,
  ): Promise<CourseListItem[]> {
    const sessionAndUser = await this.sessionService.getSessionAndUser(session);
    if (!sessionAndUser || accessToken !== sessionAndUser.session.accessToken)
      throw OakGoogleClassroomException.OakGoogleClassroomError(
        "Invalid Google Classroom session",
        "listCourses",
      );

    const oauthClient = this.oauthService.createClient(
      accessToken,
      sessionAndUser.user.refreshToken,
    );

    return new GoogleClassroomClient(this.baseUrl, oauthClient).listCourses();
  }

  /**
   * Creates a CourseWork assignment with Link material and stores the
   * assignmentToken → courseWork mapping in Firestore.
   * @param args - Lesson and course details
   * @param accessToken - The teacher's current OAuth access token
   * @param session - The teacher's encrypted session string
   */
  public async createCourseWork(
    args: Omit<CreateCourseWorkServiceArgs, "teacherLoginHint">,
    accessToken: string,
    session: string,
  ): Promise<CourseWorkCreatedResult & { assignmentToken: string }> {
    const sessionAndUser = await this.sessionService.getSessionAndUser(session);
    if (!sessionAndUser || accessToken !== sessionAndUser.session.accessToken)
      throw OakGoogleClassroomException.OakGoogleClassroomError(
        "Invalid Google Classroom session",
        "createCourseWork",
        { args },
      );

    const oauthClient = this.oauthService.createClient(
      accessToken,
      sessionAndUser.user.refreshToken,
    );

    return this.courseWorkService.createCourseWork(
      { ...args, teacherLoginHint: sessionAndUser.session.loginHint },
      oauthClient,
    );
  }

  /**
   * Retrieves the pupil's submissionId for a given coursework item.
   * Must be called with the pupil's session (userId=me lookup).
   * @param courseId - The Google Classroom course ID
   * @param courseWorkId - The courseWork item ID
   * @param accessToken - The pupil's current OAuth access token
   * @param session - The pupil's encrypted session string
   */
  public async getStudentSubmissionId(
    courseId: string,
    courseWorkId: string,
    accessToken: string,
    session: string,
  ): Promise<string> {
    const sessionAndUser = await this.sessionService.getSessionAndUser(session);
    if (!sessionAndUser || accessToken !== sessionAndUser.session.accessToken)
      throw OakGoogleClassroomException.OakGoogleClassroomError(
        "Invalid Google Classroom session",
        "getStudentSubmissionId",
      );

    const oauthClient = this.oauthService.createClient(
      accessToken,
      sessionAndUser.user.refreshToken,
    );

    return this.courseWorkProgressService.getSubmissionId(
      courseId,
      courseWorkId,
      oauthClient,
    );
  }

  /**
   * Upserts pupil progress for a link-based CourseWork assignment and syncs
   * the grade to Google Classroom when the exit quiz is newly completed.
   * @param args - The pupil progress data
   * @param accessToken - The pupil's current OAuth access token
   * @param session - The pupil's encrypted session string
   */
  public async upsertCourseWorkPupilProgress(
    args: UpsertCourseWorkPupilProgressArgs,
    accessToken: string,
    session: string,
  ): Promise<CourseWorkPupilProgress> {
    const sessionAndUser = await this.sessionService.getSessionAndUser(session);
    if (!sessionAndUser || accessToken !== sessionAndUser.session.accessToken)
      throw OakGoogleClassroomException.OakGoogleClassroomError(
        "Invalid Google Classroom session",
        "upsertCourseWorkPupilProgress",
        { args },
      );

    const resolved = await this.resolveTeacherOAuthClientForCourseWork(
      args.assignmentToken,
    );

    const pupilOAuthClient = this.oauthService.createClient(
      accessToken,
      sessionAndUser.user.refreshToken,
    );

    return this.courseWorkProgressService.upsertCourseWorkPupilProgress(
      args,
      resolved?.oauthClient,
      resolved?.courseWork,
      pupilOAuthClient,
    );
  }

  /**
   * Retrieves the stored CourseWork entity for a given assignmentToken.
   * Does not require Google Classroom authentication — Firestore-only lookup.
   * Returns null if the token is not found (caller should treat as 404).
   * @param assignmentToken - The opaque token embedded in the lesson URL
   */
  public async getClassroomCourseWork(
    assignmentToken: string,
  ): Promise<ClassroomCourseWorkEntity | null> {
    return this.firestoreClient.getClassroomCourseWork(assignmentToken);
  }

  /**
   * Retrieves pupil progress for a link-based CourseWork assignment.
   * Does not require Google Classroom authentication.
   * @param submissionId - The Google Classroom submission ID
   */
  public async getCourseWorkPupilProgress(
    submissionId: string,
    assignmentToken: string,
  ): Promise<CourseWorkPupilProgress | null> {
    return this.courseWorkProgressService.getCourseWorkPupilProgress(
      submissionId,
      assignmentToken,
    );
  }

  /**
   * Turns in a student's submission using the student's own OAuth credentials.
   * Must be called after grade sync (upsertCourseWorkPupilProgress) to ensure
   * the grade is patched before the submission state changes to TURNED_IN.
   * @param courseId - The Google Classroom course ID
   * @param courseWorkId - The courseWork item ID
   * @param submissionId - The student's submission ID
   * @param accessToken - The student's current OAuth access token
   * @param session - The student's encrypted session string
   */
  public async turnInCourseWorkSubmission(
    courseId: string,
    courseWorkId: string,
    submissionId: string,
    assignmentToken: string,
    accessToken: string,
    session: string,
  ): Promise<void> {
    const sessionAndUser = await this.sessionService.getSessionAndUser(session);
    if (!sessionAndUser || accessToken !== sessionAndUser.session.accessToken)
      throw OakGoogleClassroomException.OakGoogleClassroomError(
        "Invalid Google Classroom session",
        "turnInCourseWorkSubmission",
        { courseId, courseWorkId, submissionId },
      );

    const pupilOAuthClient = this.oauthService.createClient(
      accessToken,
      sessionAndUser.user.refreshToken,
    );

    await this.courseWorkProgressService.turnIn(
      courseId,
      courseWorkId,
      submissionId,
      assignmentToken,
      pupilOAuthClient,
    );
  }

  public async getPostSubmissionState(
    args: FetchPupilResponseArgs,
    accessToken: string,
    session: string,
  ): Promise<string> {
    const sessionAndUser = await this.sessionService.getSessionAndUser(session);
    if (!sessionAndUser || accessToken !== sessionAndUser.session.accessToken)
      throw OakGoogleClassroomException.OakGoogleClassroomError(
        "Invalid Google Classroom session",
        "getPostSubmissionStates",
        { args },
      );

    const teacherOAuthClient = await this.resolveTeacherOAuthClient(
      args.attachmentId,
    );

    if (!teacherOAuthClient) {
      console.warn(
        `Unable to resolve teacher OAuth client for attachment ${args.attachmentId}, cannot fetch post submission state`,
      );
      throw OakGoogleClassroomException.OakGoogleClassroomError(
        "Unable to resolve teacher credentials for Google Classroom API",
        "getPostSubmissionState",
        { args },
      );
    }
    const res = await this.pupilProgressService.getPostSubmissionState(
      args.courseId,
      args.itemId,
      args.attachmentId,
      args.submissionId,
      teacherOAuthClient,
    );
    return res;
  }
}
